import { usePosthogActions, usePosthogStatus } from '@/app/posthog';
import { extractSubdomain, isKeycloakEnabled } from '@/auth';
import { client } from '@/client';
import { SystemRoles } from '@/client/users';
import { LAST_SELECTED_LANGUAGE } from '@/common/constants';
import { TOKEN_EXPIRES_KEY } from '@/common/constants/user';
import { LanguageContext } from '@/common/context/LanguageContext';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIntercom } from '@/hooks/useIntercom';
import { LoadingPage } from '@/pages';
import { PendingAccessScreen } from '@/pages/pending-access/PendingAccessScreen';
import { ExpiredTrialPage } from '@/pages/trial-expired/ExpiredTrialPage';
import {
  getAccountBySubdomainAction,
  selectCurrentAccount,
  setCurrentAccount,
} from '@/store/features/account';
import {
  loadCurrentUserAction,
  selectCurrentUserState,
} from '@/store/features/users';
import { identifyHubspotContact } from '@/utils/hubspot';
import {
  buildPostHogGlobalProperties,
  buildPostHogGroupProperties,
  buildPostHogSnapshotProperties,
} from '@/utils/posthog';
import { PostHogFeatureFlags } from '@/utils/posthog/posthog.events';
import { shutdown } from '@intercom/messenger-js-sdk';
import * as Sentry from '@sentry/react';
import moment from 'moment/moment';
import { usePostHog } from 'posthog-js/react';
import { Message } from 'primereact/message';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Outlet } from 'react-router-dom';
import { Md5 } from 'ts-md5';
import userflow from 'userflow.js';

export const AuthLoadUser: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const tokenExpiresOn = sessionStorage.getItem(TOKEN_EXPIRES_KEY);
  const account = useAppSelector(selectCurrentAccount);
  const { changeLanguage, setForceChangeLanguage } =
    useContext(LanguageContext);
  const { signoutSilent } = useAuth();
  const posthog = usePostHog();
  const { isPosthogReady: isPosthogReady } = usePosthogStatus();
  const { setIsPosthogIdentified } = usePosthogActions();
  const { canUseFeature } = useFeatureFlag();
  const hasIdentifiedUserRef = useRef<string | null>(null);

  // Initialize Intercom for authenticated users
  useIntercom();

  const { loading, loaded, user, error } = useAppSelector(
    selectCurrentUserState,
  );

  useEffect(() => {
    if (
      !isKeycloakEnabled() &&
      (!tokenExpiresOn || moment(tokenExpiresOn).utc().isBefore(moment().utc()))
    ) {
      return;
    }

    loadCurrentUserAction(dispatch);
  }, [tokenExpiresOn]);

  useEffect(() => {
    if (!user) {
      hasIdentifiedUserRef.current = null;
      setIsPosthogIdentified(false);
      return;
    }

    setForceChangeLanguage(false);
    changeLanguage(user.language);

    if (
      [SystemRoles.ADMIN, SystemRoles.MULTIPLE_ACCOUNTS_ADMIN].includes(
        user?.role?.code,
      )
    ) {
      identifyHubspotContact(user);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      Sentry.setUser(null);
      Sentry.setTag('account', null);
      return;
    }

    Sentry.setUser({ id: user.id });
    Sentry.setTag('account', user.account.name);
  }, [user]);

  useEffect(() => {
    if (!user || !isPosthogReady) {
      return;
    }

    if (!user?.account.allowPosthogTracking) {
      hasIdentifiedUserRef.current = null;
      setIsPosthogIdentified(false);
      return;
    }

    if (hasIdentifiedUserRef.current === user.id) {
      setIsPosthogIdentified(true);
      return;
    }

    const hashedUserId = Md5.hashStr(user.id);
    const globalProperties = buildPostHogGlobalProperties(user.account, user);
    const snapshotProperties = buildPostHogSnapshotProperties(user.account);

    posthog.identify(hashedUserId, globalProperties, snapshotProperties);
    posthog.group(
      'account',
      user.account.isSystem ? 'system' : Md5.hashStr(user.account.id),
      buildPostHogGroupProperties(user.account),
    );
    posthog.register(globalProperties);

    hasIdentifiedUserRef.current = user.id;
    setIsPosthogIdentified(true);
  }, [user, isPosthogReady]);

  const updateCurrentAccount = async (accountId: string) => {
    const response = await client.accounts.getAccount(accountId);
    dispatch(setCurrentAccount(response));
  };

  const hasRefreshedAccount = useRef(false);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!account) {
      const subdomain = extractSubdomain(window.location);

      if (
        !!subdomain &&
        subdomain !== 'app' &&
        subdomain !== 'awareness' &&
        process.env?.REACT_APP_FORCE_CONFIG_URLS !== 'true'
      ) {
        dispatch(getAccountBySubdomainAction(window.location));
        return;
      }

      updateCurrentAccount(user.account.id);
      hasRefreshedAccount.current = true;
      return;
    }

    if (!hasRefreshedAccount.current) {
      hasRefreshedAccount.current = true;
      updateCurrentAccount(account.id);
    }
  }, [user, account]);

  useEffect(() => {
    if (!error) {
      return;
    }

    signoutSilent();
    sessionStorage.clear();
    posthog.reset();
    userflow.reset();
    shutdown(); // Clear Intercom session
  }, [error]);

  if (error) {
    return <Message text={t('user.loadingError')} severity="error" />;
  }

  if (loading || !loaded || !account) {
    return <LoadingPage message={t('user.loading')} />;
  }

  if (loaded && user?.language) {
    localStorage.setItem(LAST_SELECTED_LANGUAGE, user.language);
  }

  const userOwnAccount = user?.account;
  const isLifecycleV2 = canUseFeature(
    PostHogFeatureFlags.FREE_TRIAL_LIFECYCLE_V2,
  );
  const isExpiredTrial =
    isLifecycleV2 &&
    !!userOwnAccount?.freeTrialEndsAt &&
    moment(userOwnAccount.freeTrialEndsAt)
      .utc()
      .isSameOrBefore(moment().utc(), 'day') &&
    !!userOwnAccount?.freeTrialDeletionDate &&
    moment(userOwnAccount.freeTrialDeletionDate)
      .utc()
      .isAfter(moment().utc(), 'day');

  if (isExpiredTrial) {
    return <ExpiredTrialPage />;
  }

  const isPendingAccess =
    user?.accessState === 'pending' &&
    !!userOwnAccount?.freeTrialEndsAt &&
    canUseFeature(PostHogFeatureFlags.FREE_TRIAL_PENDING_USERS);

  if (isPendingAccess) {
    return <PendingAccessScreen />;
  }

  return <Outlet />;
};
